import type { ReactElement } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { format } from 'date-fns'
import { Check, Pencil } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { i18next } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { AnimatedValue } from '@/shared/components/ui'
import { formatMoney } from '@/shared/utils/money'
import LinkedPaymentsPopover from '@/features/indirect-costs/components/LinkedPaymentsPopover'
import IndirectCostEditPopover from '@/features/indirect-costs/components/IndirectCostEditPopover'
import { deleteIndirectCost } from '@/features/indirect-costs/api/indirectCostsService'
import type { IndirectCostEntryDTO } from '@/features/indirect-costs/schemas/indirectCostsSchema'

interface CostCardProps {
  index: number
  cost: IndirectCostEntryDTO
  linkedCount: number
  isDualColumn: boolean
  isDragActive: boolean
  recentlyLinked: boolean
}

export function CostCard({
  index,
  cost,
  linkedCount,
  isDualColumn,
  isDragActive,
  recentlyLinked,
}: CostCardProps): ReactElement {
  const { t } = useTranslation('features-indirect-costs')
  const queryClient = useQueryClient()

  const deleteMutation = useMutation({
    mutationFn: (row: IndirectCostEntryDTO) => deleteIndirectCost(row.uuid),
    onSuccess: () => {
      toast.success(t('toasts.deleted'))
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t('toasts.deleteFailed'))
    },
  })

  const { isOver, setNodeRef } = useDroppable({
    id: `cost-${cost.uuid}`,
    data: { type: 'cost', costUuid: cost.uuid },
    disabled: !isDualColumn,
  })

  const fromDate = format(new Date(cost.effectiveFrom), 'MMM dd, yyyy')
  const amountInCents = Math.round(cost.reportingAmount * 100)

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-xl border bg-card transition-all',
        isOver && 'ring-2 ring-primary/60 border-primary/40 bg-primary/5 scale-[1.01]',
        isDragActive && !isOver && 'ring-1 ring-muted-foreground/20',
        !isDragActive && !isOver && 'border-border',
      )}
    >
      <div className="flex items-stretch">
        <div
          className={cn(
            'relative flex w-12 shrink-0 items-center justify-center border-r border-border/50 transition-colors',
            recentlyLinked &&
              'bg-emerald-500/14 ring-1 ring-inset ring-emerald-400/25',
          )}
        >
          <span
            className={cn(
              'text-lg font-bold text-muted-foreground/40 transition-all duration-200',
              recentlyLinked && 'scale-90 opacity-0',
            )}
          >
            {index}
          </span>
          <Check
            className={cn(
              'pointer-events-none absolute size-4 text-[var(--border-positive)] transition-all duration-200',
              recentlyLinked ? 'scale-100 opacity-100' : 'scale-90 opacity-0',
            )}
            aria-hidden="true"
          />
        </div>
        <div className="flex flex-1 items-center justify-between gap-3 min-w-0 p-4">
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-foreground">
              {cost.subcategoryDisplayName}
            </p>
            <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground/80">
              <span className="shrink-0 text-muted-foreground/55">{fromDate}</span>
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className="text-sm font-semibold tabular-nums tracking-tight text-foreground/80">
              <AnimatedValue
                value={amountInCents}
                formatter={value =>
                  formatMoney(
                    value / 100,
                    cost.reportingCurrency,
                    i18next.language,
                    2,
                    2,
                    'narrowSymbol',
                    true,
                  )
                }
                duration={220}
                easing="easeOut"
                blur
                blurDuration={150}
              />
            </span>
            <div className="flex items-center gap-1.5">
              {linkedCount > 0 && (
                <LinkedPaymentsPopover cost={cost} />
              )}
              <IndirectCostEditPopover row={cost} onDelete={row => deleteMutation.mutate(row)}>
                <button type="button" className="glass-button rounded-md p-1.5" aria-label="Edit cost">
                  <Pencil className="size-3.5 text-muted-foreground" />
                </button>
              </IndirectCostEditPopover>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
